import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    IParticipantDraft,
    IRegistrationForm,
    IRegistrationSelections,
} from '../../Interfaces/IEventRegistration';
import { isFieldSatisfied, participantDisplayLabel } from '../registrationValidation';
import { fieldSx, GREEN_800 } from '../../core/pvtaStyles';

interface EventRegistrationAnswerFormProps {
    form: IRegistrationForm;
    participant: IParticipantDraft;
    participantIndex: number;
    onChangeSelections: (participantIndex: number, selections: IRegistrationSelections) => void;
    onChangeComment: (participantIndex: number, comment: string) => void;
    disabled?: boolean;
    /**
     * Sind die Auswahlbereiche Pflicht? Nur bei "Zusage" (Spec §9). Bei "Vielleicht"
     * darf der Nutzer speichern, ohne etwas zu waehlen -- dann waere eine rote
     * Fehlermarkierung irrefuehrend.
     */
    required?: boolean;
}

/**
 * Auswahlblock **einer** Person (Spec §7): Mitglied und jeder Gast bekommen einen
 * eigenen Block und duerfen unterschiedlich waehlen. Technische Begriffe wie
 * "RegistrationField" tauchen bewusst nirgends auf.
 */
const EventRegistrationAnswerForm: React.FC<EventRegistrationAnswerFormProps> = ({
    form,
    participant,
    participantIndex,
    onChangeSelections,
    onChangeComment,
    disabled = false,
    required = true,
}) => {
    const heading = participantIndex === 0
        ? 'Deine Auswahl'
        : participantDisplayLabel(participant, participantIndex);

    const setSingle = (fieldId: number, optionId: number): void => {
        onChangeSelections(participantIndex, {
            ...participant.selections,
            [String(fieldId)]: [optionId],
        });
    };

    const toggleMulti = (fieldId: number, optionId: number): void => {
        const current = participant.selections[String(fieldId)] ?? [];
        const next = current.includes(optionId)
            ? current.filter((id) => id !== optionId)
            : [...current, optionId];
        onChangeSelections(participantIndex, {
            ...participant.selections,
            [String(fieldId)]: next,
        });
    };

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: GREEN_800 }}>
                {heading}
            </Typography>

            <Stack spacing={2}>
                {form.fields.map((field) => {
                    const selected = participant.selections[String(field.id)] ?? [];
                    // Nur wenn die Auswahl Pflicht ist, darf ein leerer Bereich als
                    // Fehler erscheinen.
                    const showMissing = required && !isFieldSatisfied(field, participant.selections);
                    const helperText = field.selection_type === 'SINGLE'
                        ? 'Bitte genau eine Option wählen.'
                        : 'Bitte mindestens eine Option wählen.';

                    return (
                        <FormControl
                            key={field.id}
                            component="fieldset"
                            error={showMissing}
                            disabled={disabled}
                            sx={{ display: 'block' }}
                        >
                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                {field.label}
                            </FormLabel>

                            {field.selection_type === 'SINGLE' ? (
                                <RadioGroup
                                    value={selected[0] ?? ''}
                                    onChange={(event) => setSingle(field.id, Number(event.target.value))}
                                >
                                    {field.options.map((option) => (
                                        <FormControlLabel
                                            key={option.id}
                                            value={option.id}
                                            control={<Radio />}
                                            label={option.label}
                                        />
                                    ))}
                                </RadioGroup>
                            ) : (
                                <Stack>
                                    {field.options.map((option) => (
                                        <FormControlLabel
                                            key={option.id}
                                            control={
                                                <Checkbox
                                                    checked={selected.includes(option.id)}
                                                    onChange={() => toggleMulti(field.id, option.id)}
                                                />
                                            }
                                            label={option.label}
                                        />
                                    ))}
                                </Stack>
                            )}

                            {showMissing && (
                                <Typography variant="caption" color="error">
                                    {helperText}
                                </Typography>
                            )}
                        </FormControl>
                    );
                })}

                {form.comment_enabled && (
                    <TextField
                        label={form.comment_label || 'Kommentar / Hinweis'}
                        value={participant.comment}
                        onChange={(event) => onChangeComment(participantIndex, event.target.value)}
                        disabled={disabled}
                        multiline
                        rows={2}
                        size="small"
                        fullWidth
                        placeholder="z. B. Allergien oder Unverträglichkeiten"
                        sx={{ ...fieldSx }}
                    />
                )}
            </Stack>
        </Box>
    );
};

export default EventRegistrationAnswerForm;
